import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import BiometricCard from '../../../components/hardware/BiometricCard';
import BiometricDetails from '../../../components/hardware/BiometricDetails';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { Biometric as BiometricModel } from '../../../models/Biometric';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { SimpleItemProfile } from '../../../models/SimpleItemProfile';
import { TabSummary } from '../../../models/TabSummary';
import { getBiometricById, getBiometricNamesWithPagination, storeBiometric } from '../../../services/biometricService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { MultiSelectOption } from '../../../models/MultiSelectOption';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware';
const itemPath = '/access-control/hardware/biometric/';

const Biometric: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const tabContentList: ContentWrapper[] = [{
        content: <BiometricDetails />,
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }];

    const topic: Topic = {
        accessor: 'baName',
        title: translate.byKey(TranslationKeys.biometrics),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const reloadPage = async () => {
        try {
            let biometric;

            if (itemId) {
                biometric = await getBiometricById(itemId, translate);

                setSelectedItem({label: biometric.name, value: biometric.id.toString()})
                setCurrentItem(biometric);
                const updatedTabs = initialTabs;
                setTabs(updatedTabs);
            }
            else {
                setTabs([initialTabs[1]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: itemPath + `${itemId}`, displayName: `${biometric?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];

        return selectedTabContent.content;
    };

    const saveBiometric = (values) => {
        const newBiometric: BiometricModel = {
            ...currentItem,
            description: values.description,
            name: values.name,
            id: values.id,
            enabled: values.enabled,
            presetId: values.presetId,
            ipAddress: values.ipAddress,
            version: values.version,
            uploadEnabled: values.uploadEnabled,
            siteId: values.siteId,
            site: values.site,
            preset: values.preset,
            port: values.port,
            device: values.device,
            biometricTypeId: values.biometricTypeId,
            serialAddress: values.serialAddress,
            address: values.address,
            baAddress: values.address,
            musteringPoint: values.musteringPoint,
        }

        setBusy(true);
        storeBiometric(newBiometric, false)
            .then(async (value: any) => {
                setCurrentItem(newBiometric);
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.recordUpdated));
                reloadPage();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <TopicContextProvider itemId={itemId} topic={topic} dataMethod={getBiometricNamesWithPagination} selectedItem={selectedItem}>
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={saveBiometric}
                tabs={tabs}
                initialValues={currentItem}>
                <BiometricCard />
            </SimpleItemDetails>
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Biometric;
